import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Check, Trash2, CheckSquare, Pencil, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("open");
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/tasks", { params: filter === "all" ? {} : { status: filter } });
    setTasks(data); setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      const payload = { title: title.trim() };
      if (dueAt) payload.due_at = new Date(dueAt).toISOString();
      await api.post("/tasks", payload);
      setTitle(""); setDueAt("");
      toast.success("Task added");
      load();
    } catch { toast.error("Failed to add"); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/tasks/${id}`, { status });
    load();
  };
  const del = async (id) => {
    await api.delete(`/tasks/${id}`); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-6 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Tasks</h1>
          <p className="mt-1 text-sm text-slate-500">Follow-ups, checklist items, anything that needs doing.</p>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-slate-300 bg-white p-1">
          {["open", "done", "all"].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 text-xs font-semibold rounded ${filter === f ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`tasks-filter-${f}`}>{f}</button>
          ))}
        </div>
      </div>

      <form onSubmit={create} className="rounded-lg border border-slate-200 bg-white p-4 mb-6 flex items-center gap-3">
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Add a task…" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand" data-testid="task-title-input" />
        <input type="datetime-local" value={dueAt} onChange={e => setDueAt(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand" data-testid="task-due-input" />
        <button type="submit" className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="task-add-btn">
          <Plus className="h-4 w-4" /> Add
        </button>
      </form>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {loading && <div className="p-8 text-center text-sm text-slate-500">Loading…</div>}
        {!loading && tasks.length === 0 && (
          <div className="p-12 text-center" data-testid="tasks-empty">
            <CheckSquare className="h-10 w-10 mx-auto text-slate-300" />
            <div className="mt-3 font-heading text-lg font-semibold text-slate-900">
              {filter === "done" ? "Nothing checked off yet" : filter === "open" ? "You're all caught up" : "No tasks"}
            </div>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              {filter === "open"
                ? "Add follow-ups, buyer showings, listing checklist items — anything you want to be reminded of."
                : "Complete an open task to see it here."}
            </p>
          </div>
        )}
        {tasks.map(t => <TaskRow key={t.id} t={t} onChange={load} onDelete={del} onToggle={setStatus} />)}
      </div>
    </div>
  );
}

function TaskRow({ t, onChange, onDelete, onToggle }) {
  const done = t.status === "done";
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(t.title);
  const [dueAt, setDueAt] = useState(t.due_at ? t.due_at.slice(0, 16) : "");

  const save = async () => {
    if (!title.trim()) return toast.error("Title required");
    try {
      await api.patch(`/tasks/${t.id}`, {
        title: title.trim(),
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
      });
      toast.success("Task updated");
      setEditing(false);
      onChange();
    } catch { toast.error("Update failed"); }
  };

  if (editing) {
    return (
      <div className="px-6 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-3 bg-slate-50" data-testid={`task-edit-row-${t.id}`}>
        <input value={title} onChange={e => setTitle(e.target.value)} autoFocus className="input flex-1" data-testid={`task-edit-title-${t.id}`} />
        <input type="datetime-local" value={dueAt} onChange={e => setDueAt(e.target.value)} className="input" style={{ width: 200 }} data-testid={`task-edit-due-${t.id}`} />
        <button onClick={save} className="text-sm font-semibold text-white rounded-md px-3 py-1.5" style={{ background: "var(--primary-hex)" }} data-testid={`task-edit-save-${t.id}`}>Save</button>
        <button onClick={() => { setEditing(false); setTitle(t.title); setDueAt(t.due_at ? t.due_at.slice(0,16) : ""); }} className="text-slate-400 hover:text-slate-700" data-testid={`task-edit-cancel-${t.id}`}><X className="h-4 w-4" /></button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-3 border-b border-slate-100 last:border-b-0 flex items-start sm:items-center gap-3 sm:gap-4 group" data-testid={`task-row-${t.id}`}>
      <button onClick={() => onToggle(t.id, done ? "open" : "done")} className={`h-6 w-6 sm:h-5 sm:w-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0 ${done ? "bg-emerald-600 border-emerald-600" : "border-slate-300 hover:border-slate-500"}`} data-testid={`task-check-${t.id}`}>
        {done && <Check className="h-3.5 w-3.5 text-white" />}
      </button>
      <div className={`flex-1 min-w-0 text-sm ${done ? "line-through text-slate-400" : "text-slate-900"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          {t.link ? (
            <Link
              to={t.link}
              className="hover:underline inline-flex items-center gap-1.5 break-words"
              data-testid={`task-link-${t.id}`}
              title={t.client_name ? `Open ${t.client_name}'s 360-view` : "Open the related record"}
            >
              {t.title}
              <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
            </Link>
          ) : (
            <span className="break-words">{t.title}</span>
          )}
          {t.client_name && t.client_id && (
            <Link
              to={`/clients/${t.client_id}`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-400 bg-white transition-colors no-underline"
              onClick={(e) => e.stopPropagation()}
              data-testid={`task-client-${t.id}`}
              title={`Open ${t.client_name}'s 360-view`}
            >
              {t.client_name}
            </Link>
          )}
        </div>
        {t.due_at && (
          <div className="mt-1 sm:hidden text-xs text-slate-500 tabular-nums">
            {new Date(t.due_at).toLocaleString()}
          </div>
        )}
      </div>
      {t.due_at && <span className="hidden sm:inline text-xs text-slate-500 tabular-nums shrink-0">{new Date(t.due_at).toLocaleString()}</span>}
      <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-900 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0 p-1" data-testid={`task-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
      <button onClick={() => onDelete(t.id)} className="text-slate-400 hover:text-red-600 shrink-0 p-1" data-testid={`task-del-${t.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}
